"use client";

import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { PulsarService } from "@/services/pulsar.service";
import { createMainnetCampaignCollection } from "@/lib/mainnet-adapters";
import { useAuth } from "./useAuth";
import type { MainnetCampaignCollection } from "@/types/mainnet-campaign";
import { ACTIVE_MAINNET_PARTNER_IDS, MAINNET_CAMPAIGN_CONFIG } from "@/config/campaign";

export function useMainnetCampaigns(): MainnetCampaignCollection & {
  now: number;
  isLoading: boolean;
  isError: boolean;
} {
  const { isAuthenticated, getAccessToken } = useAuth();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  const overviewQuery = useQuery({
    queryKey: ["mainnet", "campaigns", MAINNET_CAMPAIGN_CONFIG.id, MAINNET_CAMPAIGN_CONFIG.groupId],
    queryFn: async () => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("Authentication is still loading.");
      }

      return PulsarService.getMainnetCampaigns(token);
    },
    enabled: isAuthenticated,
    refetchInterval: 30_000,
  });

  const partnerIds =
    overviewQuery.data?.partners
      .map((partner) => partner.partnerId)
      .filter((partnerId) =>
        (ACTIVE_MAINNET_PARTNER_IDS as readonly string[]).includes(partnerId),
      ) ?? [];

  const detailQueries = useQueries({
    queries: partnerIds.map((partnerId) => ({
      queryKey: ["mainnet", "campaigns", MAINNET_CAMPAIGN_CONFIG.id, MAINNET_CAMPAIGN_CONFIG.groupId, partnerId],
      queryFn: async () => {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication is still loading.");
        }

        return PulsarService.getMainnetCampaignDetail(partnerId, token);
      },
      enabled: isAuthenticated && !!overviewQuery.data,
      refetchInterval: 30_000,
    })),
  });

  const details = detailQueries
    .map((query) => query.data)
    .filter((detail): detail is NonNullable<typeof detail> => Boolean(detail));

  const data = useMemo<MainnetCampaignCollection>(() => {
    if (!overviewQuery.data) {
      return {
        campaigns: [],
        lastUpdatedAt: new Date().toISOString(),
      };
    }

    return createMainnetCampaignCollection(
      {
        ...overviewQuery.data,
        partners: overviewQuery.data.partners.filter((partner) =>
          (ACTIVE_MAINNET_PARTNER_IDS as readonly string[]).includes(partner.partnerId),
        ),
      },
      details,
    );
  }, [details, overviewQuery.data]);

  const isLoading =
    overviewQuery.isLoading ||
    (partnerIds.length > 0 && detailQueries.some((query) => query.isLoading && !query.data));

  const isError =
    overviewQuery.isError ||
    detailQueries.some((query) => query.isError);

  return {
    ...data,
    now,
    isLoading,
    isError,
  };
}
